import { Abstract01Icon } from '@components/Icons/abstract-icons/Abstract01Icon';
import { Abstract02Icon } from '@components/Icons/abstract-icons/Abstract02Icon';
import { Abstract03Icon } from '@components/Icons/abstract-icons/Abstract03Icon';
import { Abstract04Icon } from '@components/Icons/abstract-icons/Abstract04Icon';
import { Abstract05Icon } from '@components/Icons/abstract-icons/Abstract05Icon';
import { Abstract06Icon } from '@components/Icons/abstract-icons/Abstract06Icon';
import { Abstract07Icon } from '@components/Icons/abstract-icons/Abstract07Icon';
import { Abstract08Icon } from '@components/Icons/abstract-icons/Abstract08Icon';
import classNames from 'classnames';
import css from './LibraryCardPlaceholder.module.scss';

const ABSTRACT_ICONS = [
  Abstract01Icon,
  Abstract02Icon,
  Abstract03Icon,
  Abstract04Icon,
  Abstract05Icon,
  Abstract06Icon,
  Abstract07Icon,
  Abstract08Icon,
];

// Brand-family hues only. Red (#D64550 = danger) and orange (#FF6733) were
// status colors used decoratively, so an empty library could land on a
// red cover and read like an error. These are purple/violet/teal/orchid —
// varied but on-brand (status colors stay functional, not decorative).
const BACKGROUND_COLORS = ['#9D4EDD', '#7A3CB5', '#4FD1D9', '#B471E6'];

interface LibraryCardPlaceholderProps {
  seed: string | number;
  /**
   * `cover` (default) is the library card's framing: the icon spans the full width
   * and anchors to the top, leaving the lower half of the tall card for the name
   * and gradient.
   *
   * `contain` centres the icon and bounds it on both axes, for wide-and-short
   * thumbnails (table cells, list rows). The abstract icons are fixed-size SVGs
   * (130×125) that fit a 210px-wide card naturally but overflow an 85×48 cell, so
   * the compact case has to scale them down rather than just re-anchor them.
   */
  fit?: 'cover' | 'contain';
}

/**
 * Deterministic abstract cover for libraries with no usable thumbnail. The seed
 * (normally the library ID) picks the icon and background, so a given library
 * always gets the same cover.
 */
export const LibraryCardPlaceholder = ({ seed, fit = 'cover' }: LibraryCardPlaceholderProps) => {
  const num = seed
    .toString()
    .split('')
    .reduce((acc, char) => {
      return acc + char.charCodeAt(0) * 37 + 1;
    }, 0);

  const colorIndex = Math.floor(num / ABSTRACT_ICONS.length) % BACKGROUND_COLORS.length;
  const iconIndex = (num + 3) % ABSTRACT_ICONS.length;

  const IconComponent = ABSTRACT_ICONS[iconIndex];
  const backgroundColor = BACKGROUND_COLORS[colorIndex];

  return (
    <div
      className={classNames(css.libraryCardPlaceholder, { [css.contain]: fit === 'contain' })}
      style={{
        backgroundColor,
      }}
    >
      <div className={css.iconContainer}>
        <IconComponent />
      </div>
    </div>
  );
};
